// src/client/components/settings/SettingsHeader.tsx
import React from 'react';
import { Button } from '@client/components/ui/button';
import { Input } from '@client/components/ui/input';
import { Card, CardContent } from '@client/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@client/components/ui/alert-dialog';
import { Plus, Search, RotateCcw } from 'lucide-react';
import { CrudDialog } from '@client/components/shared/CrudDialog';
import { FormField } from '@client/components/shared/FormField';
import type { NewSettingForm } from '@client/types/settings';
import { validateSettingKey, validateSettingValue } from '@client/types/settings';

interface SettingsHeaderProps {
  searchFilter: string;
  setSearchFilter: (value: string) => void;
  showNewSettingDialog: boolean;
  setShowNewSettingDialog: (show: boolean) => void;
  newSetting: NewSettingForm;
  setNewSetting: React.Dispatch<React.SetStateAction<NewSettingForm>>;
  createSetting: (setting: NewSettingForm) => Promise<void>;
  resetToDefaults: () => Promise<void>;
}

export default function SettingsHeader({
  searchFilter,
  setSearchFilter,
  showNewSettingDialog,
  setShowNewSettingDialog,
  newSetting,
  setNewSetting,
  createSetting,
  resetToDefaults
}: SettingsHeaderProps) {
  const [formError, setFormError] = React.useState<string | null>(null);

  const handleSave = () => {
    // Validate
    const keyError = validateSettingKey(newSetting.key);
    if (keyError) {
      setFormError(keyError);
      return;
    }

    const valueError = validateSettingValue(newSetting.value, newSetting.type);
    if (valueError) {
      setFormError(valueError);
      return;
    }

    setFormError(null);
    createSetting(newSetting);
  };

  const handleCancel = () => {
    setShowNewSettingDialog(false);
    setFormError(null);
  };

  return (
    <>
      {/* Page Header */}
      <header className="bg-white dark:bg-gray-900 shadow-sm border-b border-slate-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-end">
          <Button onClick={() => setShowNewSettingDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Setting
          </Button>
        </div>
      </header>

      {/* Search and Actions */}
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search settings..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="pl-10"
                />
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline">
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset to Defaults
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reset to Default Settings?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will delete all current settings and restore the default configuration. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={resetToDefaults}
                      className="bg-destructive hover:bg-destructive/90"
                    >
                      Reset Settings
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Setting Dialog */}
      <CrudDialog
        open={showNewSettingDialog}
        onOpenChange={setShowNewSettingDialog}
        mode="create"
        title="Add New Setting"
        description="Create a new configuration setting"
        onSave={handleSave}
        onCancel={handleCancel}
      >
        <FormField
          id="setting-key"
          label="Key"
          value={newSetting.key}
          onChange={(value) => setNewSetting(prev => ({ ...prev, key: String(value) }))}
          placeholder="e.g., features.module.enabled"
          required
        />

        <FormField
          id="setting-type"
          label="Type"
          type="select"
          value={newSetting.type}
          onChange={(value) => setNewSetting(prev => ({ ...prev, type: value as any }))}
          options={[
            { value: 'string', label: 'String' },
            { value: 'boolean', label: 'Boolean' },
            { value: 'number', label: 'Number' },
            { value: 'json', label: 'JSON' },
          ]}
        />

        <FormField
          id="setting-value"
          label="Value"
          type={newSetting.type === 'boolean' ? 'select' : newSetting.type === 'json' ? 'textarea' : newSetting.type === 'number' ? 'number' : 'text'}
          value={newSetting.value}
          onChange={(value) => setNewSetting(prev => ({ ...prev, value: String(value) }))}
          placeholder={newSetting.type === 'json' ? '{"key": "value"}' : 'Enter value...'}
          options={newSetting.type === 'boolean' ? [
            { value: 'true', label: 'True' },
            { value: 'false', label: 'False' },
          ] : undefined}
          className={newSetting.type === 'json' ? 'font-mono' : ''}
        />

        {formError && <p className="text-sm text-red-500">{formError}</p>}
      </CrudDialog>
    </>
  );
}